import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import { invalidateResource, type components } from '../api';

type Schemas = components['schemas'];
type LiveEvent = Schemas['LiveEvent'];
type NotificationPage = Schemas['NotificationPage'];
type IntegrationPage = Schemas['IntegrationPage'];

// A price re-read sends one event per changed variant, each naming its product (live updates §4):
// the products list, and the data-quality report, are invalidated at most once per 2 s (UI
// architecture §4).
const THROTTLE_WINDOW_MS = 2_000;

// $api's keys (UI architecture §4): every GET of a path whatever its parameters (for a path with an
// id, every id), or the GET of one path id.
const byPath = (path: string): QueryKey => ['get', path];
const byId = (path: string, id: string): QueryKey => ['get', path, { params: { path: { id } } }];

// Every test chat's thread: the events never name its session.
const testChats = byPath('/api/test-chat/sessions/{id}/messages');
// What the billing events refetch; all but card.expiring add /api/me (a suspension changes what the
// panel allows) and onboarding (step 8).
const billing = [byPath('/api/billing'), byPath('/api/invoices'), byPath('/api/home')];
// What settings.changed refetches: the AI Center and Settings screens, onboarding, Home, and every
// conversation view (the mode decides its actions).
const settings = [
  '/api/assistant/status',
  '/api/knowledge',
  '/api/handoff-rules',
  '/api/voice',
  '/api/sales/settings',
  '/api/settings/business',
  '/api/notification-preferences',
  '/api/integrations',
  '/api/onboarding',
  '/api/home',
  '/api/conversations/{id}',
].map(byPath);

function invalidate(queryClient: QueryClient, ...queryKeys: QueryKey[]): void {
  for (const queryKey of queryKeys) void queryClient.invalidateQueries({ queryKey });
}

// Events carry no message body (live updates §3): the thread refetches (at once when it is open on
// screen, as an active query), and so does every conversation list (unread flag, status, timer).
function messagesChanged(queryClient: QueryClient, id: string): void {
  invalidate(
    queryClient,
    byId('/api/conversations/{id}/messages', id),
    byPath('/api/conversations'),
  );
}

function messageCreated(
  queryClient: QueryClient,
  data: Schemas['MessageCreatedEvent']['data'],
): void {
  messagesChanged(queryClient, data.conversation_id);
  if (data.domain_event === 'message.received') {
    // The event names the conversation, not the Shopper nor the message's reference kind: every
    // Shopper profile refetches (a new row, the last contact, the conversations card), and so do
    // the content grid's ask counts.
    invalidate(
      queryClient,
      byPath('/api/opportunities'),
      byPath('/api/shoppers'),
      byPath('/api/shoppers/{id}'),
      byPath('/api/content'),
    );
  }
  if (data.test) invalidate(queryClient, testChats);
}

// Waiting's rows, the conversation's badge and status in its view and in the inbox list, and Home's
// waiting count.
function handoffChanged(queryClient: QueryClient, id: string): void {
  invalidate(
    queryClient,
    byPath('/api/handoffs'),
    byId('/api/conversations/{id}', id),
    byPath('/api/conversations'),
    byPath('/api/home'),
  );
}

type ThrottleWindow = { trailing: boolean; timer: ReturnType<typeof setTimeout> };

// Per QueryClient and path: the current 2-second window, and whether another event came inside it.
const throttleWindows = new WeakMap<QueryClient, Map<string, ThrottleWindow>>();

// Invalidates every GET of the path at once, then once more at the end of its 2-second window when
// another event came inside it.
function invalidateThrottled(queryClient: QueryClient, path: string): void {
  const windows = throttleWindows.get(queryClient) ?? new Map<string, ThrottleWindow>();
  throttleWindows.set(queryClient, windows);
  const current = windows.get(path);
  if (current) {
    current.trailing = true;
    return;
  }
  invalidate(queryClient, byPath(path));
  const next: ThrottleWindow = {
    trailing: false,
    timer: setTimeout(() => {
      windows.delete(path);
      if (next.trailing) invalidateThrottled(queryClient, path);
    }, THROTTLE_WINDOW_MS),
  };
  windows.set(path, next);
}

/**
 * Drops every 2-second window (the products list's, the data-quality report's) and its trailing
 * invalidation (the stream closes).
 */
export function clearProductListWindow(queryClient: QueryClient): void {
  for (const { timer } of throttleWindows.get(queryClient)?.values() ?? []) clearTimeout(timer);
  throttleWindows.delete(queryClient);
}

// Every catalog event names its product, price and stock events too (D-090): its detail refetches,
// and the products list at most once per 2 s.
function productChanged(queryClient: QueryClient, id: string): void {
  invalidate(queryClient, byId('/api/products/{id}', id));
  invalidateThrottled(queryClient, '/api/products');
}

// A connection's status shows on Integrations and Home, whatever the domain event; a channel's and
// a store's are onboarding steps too, and a channel's sets the assistant's mode per connection (the
// AI Center status).
function connectionChanged(
  queryClient: QueryClient,
  data: Schemas['ConnectionChangedEvent']['data'],
): void {
  invalidate(queryClient, byPath('/api/integrations'), byPath('/api/home'));
  switch (data.domain_event) {
    case 'channel.connection.changed':
      return invalidate(queryClient, byPath('/api/onboarding'), byPath('/api/assistant/status'));
    case 'store.connection.changed':
      return invalidate(queryClient, byPath('/api/onboarding'));
  }
}

function conversationUpdated(
  queryClient: QueryClient,
  data: Schemas['ConversationUpdatedEvent']['data'],
): void {
  const id = data.conversation_id;
  switch (data.domain_event) {
    case 'message.delivered':
    case 'message.failed':
      return messagesChanged(queryClient, id);
    case 'message.cancelled':
      // The thread and the lists, as for a delivery; the conversation view too (the timeline's note).
      messagesChanged(queryClient, id);
      return invalidate(queryClient, byId('/api/conversations/{id}', id));
    case 'conversation.ownership.changed':
      // The open view's action bar follows the owner.
      return invalidate(
        queryClient,
        byId('/api/conversations/{id}', id),
        byPath('/api/conversations'),
        byPath('/api/opportunities'),
      );
    case 'conversation.handoff.claimed':
    case 'conversation.handoff.resolved':
      return handoffChanged(queryClient, id);
    case 'conversation.outcome.marked':
      // The event names the conversation, not the Shopper: every Shopper profile refetches.
      return invalidate(
        queryClient,
        byPath('/api/opportunities'),
        byPath('/api/sales'),
        byId('/api/conversations/{id}', id),
        byPath('/api/conversations'),
        byPath('/api/shoppers/{id}'),
      );
    case 'attribution.decided':
    case 'attribution.reversed':
      return invalidate(
        queryClient,
        byPath('/api/sales'),
        byPath('/api/sales/summary'),
        byPath('/api/opportunities'),
        byId('/api/conversations/{id}', id),
        byPath('/api/shoppers/{id}'),
        byPath('/api/home'),
      );
    default:
      // A delivery change that arrives without its domain event behaves like that event.
      if (data.change === 'message_delivered' || data.change === 'message_failed') {
        return messagesChanged(queryClient, id);
      }
      if (data.change === 'message_cancelled') {
        messagesChanged(queryClient, id);
        return invalidate(queryClient, byId('/api/conversations/{id}', id));
      }
      // Inbox's own assign, note, close and reopen, which carry no domain event.
      void invalidateResource(queryClient, '/api/conversations', id);
  }
}

function domainEvent(
  queryClient: QueryClient,
  data: Schemas['DomainEventForwarded']['data'],
): void {
  const { subject } = data;
  switch (data.domain_event) {
    case 'catalog.product.changed':
      productChanged(queryClient, subject.id);
      // The data-quality report of onboarding step 2, within the same limit.
      return invalidateThrottled(queryClient, '/api/products/data-quality');
    case 'catalog.product.deleted':
      productChanged(queryClient, subject.id);
      // A content item linked to the product returns to review.
      return invalidate(queryClient, byPath('/api/content/{id}'));
    case 'catalog.price.changed':
    case 'catalog.stock.changed':
      return productChanged(queryClient, subject.id);
    case 'content.captured':
      return void invalidateResource(queryClient, '/api/content', subject.id);
    case 'content.linked':
      // A product's linked content: the event names the content item, not the product.
      invalidate(queryClient, byPath('/api/products/{id}'));
      return void invalidateResource(queryClient, '/api/content', subject.id);
    case 'store.sync.completed':
      // Onboarding step 2 enables "Devam et" once last_full_sync_at is set (UI architecture §4).
      return invalidate(
        queryClient,
        byPath('/api/integrations'),
        byPath('/api/onboarding'),
        byPath('/api/products/data-quality'),
      );
    case 'usage.threshold.crossed':
      return invalidate(queryClient, byPath('/api/usage'), byPath('/api/home'));
    case 'subscription.changed':
    case 'payment.succeeded':
    case 'payment.failed':
    case 'invoice.issued':
      return invalidate(queryClient, ...billing, byPath('/api/me'), byPath('/api/onboarding'));
    case 'card.expiring':
      return invalidate(queryClient, ...billing);
    case 'assistant.gap.recorded':
      return invalidate(queryClient, byPath('/api/gaps'), byPath('/api/reports/{reportId}'));
    case 'link.opened':
      return invalidate(queryClient, byPath('/api/sales/summary'));
    case 'settings.changed':
      return invalidate(queryClient, ...settings);
  }
}

// The bell's lists get the new notification on top. The event carries no body and no payload
// (live updates §3): both stay empty until the next refetch. A notification already listed is left
// alone, so a replayed event changes nothing.
function prependNotification(
  queryClient: QueryClient,
  event: Schemas['NotificationCreatedEvent'],
): void {
  const { notification_id, notification_type, title, url } = event.data;
  const notification: Schemas['Notification'] = {
    notification_id,
    type: notification_type,
    title,
    url,
    body: '',
    payload: {},
    created_at: event.occurred_at,
    read_at: null,
  };
  const prepend = (page: NotificationPage): NotificationPage | undefined =>
    page.items.some((item) => item.notification_id === notification_id)
      ? undefined
      : { ...page, items: [notification, ...page.items], unread_count: page.unread_count + 1 };
  queryClient.setQueriesData<NotificationPage | InfiniteData<NotificationPage>>(
    { queryKey: byPath('/api/notifications') },
    (data) => {
      if (data === undefined) return undefined;
      if (!('pages' in data)) return prepend(data);
      const [first, ...rest] = data.pages;
      const patched = first && prepend(first);
      return patched && { ...data, pages: [patched, ...rest] };
    },
  );
}

// Progress events are frequent, so the connection's run is patched in place, never refetched. A
// run the cache does not hold cannot be patched: the connections refetch instead.
function patchSyncProgress(
  queryClient: QueryClient,
  data: Schemas['SyncProgressEvent']['data'],
): void {
  const { store_connection_id, run_id, stream, done, total } = data;
  let patched = false;
  queryClient.setQueriesData<IntegrationPage>({ queryKey: byPath('/api/integrations') }, (page) => {
    const connection = page?.items.find(
      (item) => item.connection_id === store_connection_id && item.last_run?.run_id === run_id,
    );
    if (!page || !connection?.last_run) return undefined;
    patched = true;
    const progressed = {
      ...connection,
      last_run: { ...connection.last_run, progress: { stream, done, total } },
    };
    return { ...page, items: page.items.map((item) => (item === connection ? progressed : item)) };
  });
  if (!patched) invalidate(queryClient, byPath('/api/integrations'));
}

/**
 * Runs the cache action of a stream event (UI architecture §4): keyed by the domain event it
 * carries, else by its type. Every action invalidates or patches by id, so an event delivered twice
 * (live updates §3) leaves the same cache. Control events have none.
 */
export function applyCacheAction(queryClient: QueryClient, event: LiveEvent): void {
  switch (event.type) {
    case 'message.created':
      return messageCreated(queryClient, event.data);
    case 'conversation.updated':
      return conversationUpdated(queryClient, event.data);
    case 'handoff.created':
      handoffChanged(queryClient, event.data.conversation_id);
      // The holding reply and its handoff badge in the test chat.
      return invalidate(queryClient, testChats);
    case 'draft.created':
      // The draft card in the test chat.
      invalidate(queryClient, testChats);
      return void invalidateResource(queryClient, '/api/conversations', event.data.conversation_id);
    case 'notification.created':
      return prependNotification(queryClient, event);
    case 'sync.progress':
      return patchSyncProgress(queryClient, event.data);
    case 'connection.changed':
      return connectionChanged(queryClient, event.data);
    case 'domain.event':
      return domainEvent(queryClient, event.data);
  }
}
